import { useState } from "react";

/**
 * Day-type tab sets — shared across the three admin forms so a day-type key
 * (e.g. "saturday_am") always means the same thing to pricingEngine.js
 * regardless of which form wrote it.
 */
export const COURSE_DAY_TYPES = [
  { key: "weekday", label: "Weekdays" },
  { key: "saturday_am", label: "Saturday AM" },
  { key: "saturday_pm", label: "Saturday PM" },
  { key: "sunday_am", label: "Sunday AM" },
  { key: "sunday_pm", label: "Sunday PM" },
  { key: "holiday_am", label: "Public Holiday AM" },
  { key: "holiday_pm", label: "Public Holiday PM" },
];

export const ACCOMMODATION_DAY_TYPES = [
  { key: "weekday", label: "Weekdays" },
  { key: "friday_am", label: "Friday AM" },
  { key: "friday_pm", label: "Friday PM" },
  { key: "saturday_am", label: "Saturday AM" },
  { key: "saturday_pm", label: "Saturday PM" },
  { key: "sunday_am", label: "Sunday AM" },
  { key: "sunday_pm", label: "Sunday PM" },
  { key: "holiday_am", label: "Public Holiday AM" },
  { key: "holiday_pm", label: "Public Holiday PM" },
];

/**
 * @param {{
 *   dayTypes: {key:string,label:string}[],
 *   rows: {key:string,label:string}[],
 *   columns: {key:string,label:string}[],
 *   value: object,   // { [dayTypeKey]: { [rowKey]: { [colKey]: number|string } } }
 *   onChange: (next:object) => void,
 * }} props
 */
export default function MatrixPriceEditor({ dayTypes, rows, columns, value, onChange }) {
  const [activeTab, setActiveTab] = useState(dayTypes[0].key);
  const matrix = value || {};

  const setCell = (rowKey, colKey, raw) => {
    onChange({
      ...matrix,
      [activeTab]: {
        ...matrix[activeTab],
        [rowKey]: {
          ...matrix[activeTab]?.[rowKey],
          [colKey]: raw,
        },
      },
    });
  };

  return (
    <div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-4 -mx-1 px-1">
        {dayTypes.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => setActiveTab(d.key)}
            className={`h-9 px-4 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              activeTab === d.key
                ? "bg-[#0B2421] text-white"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left font-semibold text-[#0B2421] px-4 py-2.5 whitespace-nowrap">
                {rows[0]?.groupLabel || "\u00A0"}
              </th>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="text-left font-semibold text-[#0B2421] px-4 py-2.5 whitespace-nowrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} className={i > 0 ? "border-t border-gray-100" : ""}>
                <td className="px-4 py-2.5 font-medium text-gray-700 whitespace-nowrap">
                  {r.label}
                </td>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37] font-semibold text-xs">
                        SGD
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={matrix[activeTab]?.[r.key]?.[c.key] ?? ""}
                        onChange={(e) => setCell(r.key, c.key, e.target.value)}
                        placeholder="0.00"
                        className="w-full h-10 rounded-lg border border-gray-200 bg-gray-50 pl-12 pr-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}